import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const LogicCharts = ({ section }) => {
    // 1. Completion Pie
    const completionData = [
        { name: 'Yechilgan', value: section.solved_problems, color: '#2ecc71' },
        { name: 'Qolgan', value: section.total_problems - section.solved_problems, color: '#e74c3c' }
    ];

    // 2. Bar Chart for Problem Times
    // Filter only solved problems or show all? Show solved only to keep it clean.
    const solvedProbs = section.problems.filter(p => p.is_solved);
    const timeData = solvedProbs.map((p, index) => ({
        name: `M-${p.id}`, // Masala ID
        vaqt: p.solve_time, // seconds
        full_name: p.nomi
    }));

    // 3. Radar Data (Skills)
    // - Progress: % solved
    // - Independence: 100 - (% of problems where video was watched)
    // - Speed: Avg time compared to a baseline (say 10 mins per problem is standard)

    const progressScore = (section.progress / 10);

    const problemsWithVideo = solvedProbs.filter(p => p.used_video_help).length;
    const videoUsageRatio = solvedProbs.length > 0 ? (problemsWithVideo / solvedProbs.length) : 0;
    const independenceScore = 10 * (1 - videoUsageRatio);

    // Speed: Avg time compared to difficulty baseline.
    // Difficulty sum of solved problems:
    let totalDifficulty = 0;
    solvedProbs.forEach(p => totalDifficulty += (p.difficulty || 1));
    const avgDifficulty = solvedProbs.length > 0 ? (totalDifficulty / solvedProbs.length) : 1;

    // Baseline Time (seconds) = Difficulty * 5 minutes (300s)
    // E.g. Diff 1 -> 300s, Diff 2 -> 600s, Diff 3 -> 900s
    const baselineTime = avgDifficulty * 300;

    // Avg User Time
    const avgUserTime = solvedProbs.length > 0 ? (section.total_time_spent / solvedProbs.length) : 0;

    // Speed Score: 10 * (Baseline / UserTime). Cap at 10.
    // If UserTime is half of Baseline, score is 20 (capped 10).
    // If UserTime is equal Baseline, score is 10.
    // If UserTime is double Baseline, score is 5.
    let speedScore = 0;
    if (avgUserTime > 0) {
        speedScore = 10 * (baselineTime / avgUserTime);
        // Normalize: if equal to baseline, give 8. If faster, go to 10.
        // Let's adjust: 
        // Score = 8 + (Baseline - UserTime)/Baseline * 4  => range
        // Simpler: 
        // Ratio = Baseline / UserTime. Ideal > 1.
        // Score = Ratio * 8.
        speedScore = (baselineTime / avgUserTime) * 8;
    }

    if (speedScore > 10) speedScore = 10;
    if (speedScore < 1 && solvedProbs.length > 0) speedScore = 1;


    const radarData = [
        { subject: 'Progress', A: progressScore, fullMark: 10 },
        { subject: 'Mustaqillik', A: independenceScore, fullMark: 10 }, // No video help
        { subject: 'Tezlik', A: speedScore, fullMark: 10 },
        { subject: 'Barqarorlik', A: Math.min(solvedProbs.length, 10), fullMark: 10 },
        { subject: 'Sifat', A: 8, fullMark: 10 }, // Placeholder
    ];

    return (
        <div className="charts-container" style={{ padding: '20px' }}>


            <div className="charts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }}>

                {/* O'zlashtirish */}
                <div className="chart-card" style={cardStyle}>
                    <h4>Yechimlar Foizi</h4>
                    <div style={{ width: '100%', height: 250 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={completionData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={70}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {completionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Radar (Skills) */}
                <div className="chart-card" style={cardStyle}>
                    <h4>Mantiqiy Salohiyat</h4>
                    <div style={{ width: '100%', height: 250 }}>
                        <ResponsiveContainer>
                            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="subject" />
                                <PolarRadiusAxis angle={30} domain={[0, 10]} />
                                <Radar name="Natija" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                                <Legend />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Bar Chart (Time) */}
                <div className="chart-card" style={{ ...cardStyle, gridColumn: '1 / -1' }}>
                    <h4>Har Bir Masala Uchun Sarflangan Vaqt (soniya)</h4>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={timeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey="vaqt" name="Vaqt (soniya)" fill="#3498db" radius={[5, 5, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{ background: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }}>
                <p style={{ fontWeight: 'bold' }}>{label}</p>
                {payload.length > 0 && payload[0].payload.full_name && (
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>{payload[0].payload.full_name}</p>
                )}
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }}>
                        {entry.name}: {entry.value}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const cardStyle = {
    background: '#fff',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    border: '1px solid #eee'
};

export default LogicCharts;
